import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BusinessOutlined,
  PhoneInTalkOutlined,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { salesPortalService } from '../../services/salesPortalService';
import {
  SalesCard,
  SalesEmptyState,
  SalesEntityAvatar,
  SalesPageHeader,
  SALES,
  salesPrimaryButtonSx,
  salesRowHoverSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const HEADER_FILTERS_INITIAL = {
  custNo: '',
  company: '',
  phone: '',
  location: '',
  accountRep: '',
};

const colHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.375,
  width: '100%',
};

const colHeaderLabelSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  lineHeight: 1.25,
  color: 'inherit',
  whiteSpace: 'nowrap',
};

const headerFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1, backgroundColor: SALES.white },
  '& .MuiInputBase-input': { py: 0.35 },
};

const trimHeaderFilterObj = (obj) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '').trim()]));

const accountRepName = (row) => {
  if (!row.account_rep_employee_id) return '';
  return `${row.account_rep_first_name || ''} ${row.account_rep_last_name || ''}`.trim();
};

const SalesCustomers = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [headerColFilters, setHeaderColFilters] = useState(() => ({ ...HEADER_FILTERS_INITIAL }));
  const [debouncedHeaderColFilters, setDebouncedHeaderColFilters] = useState(() => ({
    ...HEADER_FILTERS_INITIAL,
  }));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const raw = parseInt(window.localStorage.getItem('wm_sales_customers_rows_per_page'), 10);
      return [25, 50, 100].includes(raw) ? raw : 50;
    } catch {
      return 50;
    }
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await salesPortalService.getCustomers({ limit: 500 });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = trimHeaderFilterObj(headerColFilters);
      setDebouncedHeaderColFilters((prev) => {
        const same = Object.keys(HEADER_FILTERS_INITIAL).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [headerColFilters]);

  useEffect(() => {
    setPage(0);
  }, [debouncedHeaderColFilters]);

  const filteredRows = useMemo(() => {
    const f = debouncedHeaderColFilters;
    const includes = (value, needle) =>
      !needle || String(value ?? '').toLowerCase().includes(needle.toLowerCase());

    return rows.filter((row) => {
      const location = [row.city, row.state].filter(Boolean).join(', ');
      const company = [row.company_name, row.short_name].filter(Boolean).join(' ');
      return (
        includes(row.customer_number, f.custNo) &&
        includes(company, f.company) &&
        includes(row.phone, f.phone) &&
        includes(location, f.location) &&
        includes(accountRepName(row), f.accountRep)
      );
    });
  }, [rows, debouncedHeaderColFilters]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const hasActiveFilters = Object.values(debouncedHeaderColFilters).some(Boolean);

  const openLogForCustomer = (customerId, e) => {
    e?.stopPropagation?.();
    navigate(`/sales/activity/new?customer_id=${customerId}`);
  };

  const renderHeaderFilter = (key, placeholder = 'Filter…') => (
    <TextField
      size="small"
      placeholder={placeholder}
      value={headerColFilters[key]}
      onChange={(e) => setHeaderColFilters((p) => ({ ...p, [key]: e.target.value }))}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      sx={headerFilterTextFieldSx}
    />
  );

  return (
    <Box>
      <SalesPageHeader
        title="Customers"
        subtitle="Browse existing Fab Works accounts for relationship building, visit logs, and quote requests."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<PhoneInTalkOutlined />}
              onClick={() => navigate('/sales/activity/new')}
              sx={salesPrimaryButtonSx}
            >
              Log Call / Visit
            </Button>
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <SalesEmptyState
          icon={<BusinessOutlined sx={{ fontSize: 42 }} />}
          title="No customers found"
          description="Ask Admin if the account has been set up yet, or log a call against a new lead."
          actionLabel="Log Call / Visit"
          onAction={() => navigate('/sales/activity/new')}
        />
      ) : filteredRows.length === 0 ? (
        <SalesEmptyState
          icon={<BusinessOutlined sx={{ fontSize: 42 }} />}
          title="No matching customers"
          description="Try clearing the column filters above the table."
        />
      ) : (
        <SalesCard sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: `1px solid ${SALES.line}`,
              backgroundColor: SALES.subtle,
            }}
          >
            <Typography variant="body2" sx={{ color: SALES.muted }}>
              {hasActiveFilters
                ? `Showing ${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredRows.length)} of ${filteredRows.length} matching customer${filteredRows.length === 1 ? '' : 's'}`
                : `Showing ${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, filteredRows.length)} of ${filteredRows.length} customer${filteredRows.length === 1 ? '' : 's'}`}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={salesTableHeadSx}>
                <TableRow>
                  <TableCell sx={{ minWidth: 100, verticalAlign: 'top' }}>
                    <Box sx={colHeaderStackSx}>
                      <Typography sx={colHeaderLabelSx}>Cust #</Typography>
                      {renderHeaderFilter('custNo')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 220, verticalAlign: 'top' }}>
                    <Box sx={colHeaderStackSx}>
                      <Typography sx={colHeaderLabelSx}>Company</Typography>
                      {renderHeaderFilter('company')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 130, verticalAlign: 'top' }}>
                    <Box sx={colHeaderStackSx}>
                      <Typography sx={colHeaderLabelSx}>Phone</Typography>
                      {renderHeaderFilter('phone')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 140, verticalAlign: 'top' }}>
                    <Box sx={colHeaderStackSx}>
                      <Typography sx={colHeaderLabelSx}>Location</Typography>
                      {renderHeaderFilter('location')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150, verticalAlign: 'top' }}>
                    <Box sx={colHeaderStackSx}>
                      <Typography sx={colHeaderLabelSx}>Account Rep</Typography>
                      {renderHeaderFilter('accountRep')}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 72, verticalAlign: 'top' }}>
                    <Typography sx={colHeaderLabelSx}>Actions</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={salesRowHoverSx}
                    onClick={() => navigate(`/sales/customers/${row.id}`)}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, color: SALES.steel }}>
                        #{row.customer_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <SalesEntityAvatar name={row.company_name} />
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.company_name}</Typography>
                          {row.short_name ? (
                            <Typography variant="caption" sx={{ color: SALES.muted }}>
                              {row.short_name}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.phone || '—'}</TableCell>
                    <TableCell>
                      {[row.city, row.state].filter(Boolean).join(', ') || '—'}
                    </TableCell>
                    <TableCell>{accountRepName(row) || '—'}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Log call / visit">
                        <IconButton
                          size="small"
                          aria-label={`Log call for ${row.company_name}`}
                          onClick={(e) => openLogForCustomer(row.id, e)}
                          sx={{
                            color: SALES.steel,
                            '&:hover': { backgroundColor: SALES.steelSoft },
                          }}
                        >
                          <PhoneInTalkOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setRowsPerPage(next);
              setPage(0);
              try {
                window.localStorage.setItem('wm_sales_customers_rows_per_page', String(next));
              } catch {
                // ignore storage failures
              }
            }}
            sx={{
              borderTop: `1px solid ${SALES.line}`,
              color: SALES.muted,
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                color: SALES.muted,
              },
            }}
          />
        </SalesCard>
      )}
    </Box>
  );
};

export default SalesCustomers;
